/**
 * RequestsView — Admin-Ansicht für Projekt-Anfragen (Kontaktformular).
 *
 * Bewusst auf AdminListView aufgesetzt, damit die Anfragen-Liste GENAU die
 * Funktionen der Bestellungen-Ansicht hat: Suchfeld, Filter-Dropdowns und das
 * Zahnrad mit Zeilen-Größe, Spaltenwahl und Status-Gruppierung — alles in
 * localStorage gemerkt (Key `aether_list_requests`).
 *
 * Obendrauf ein Detail-Panel je Anfrage:
 *   - Statuswechsel (das Backend mailt dem Anfragenden bei JEDEM Wechsel)
 *   - Haken „Kunde benachrichtigen", falls man mal still umstellen will
 *   - interne Notizen + Verlauf (wer wann was geändert hat)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminListView from './AdminListView';
import {
  Inbox, X, Mail, Phone, Building2, Euro, CalendarClock, Copy, Check,
  Loader2, RefreshCw, StickyNote, ExternalLink, AlertCircle, Send, BellOff,
} from 'lucide-react';

/* Reihenfolge = Workflow-Reihenfolge, identisch zu REQUEST_STATUSES im Backend. */
const STATUS_ORDER = ['neu', 'in_bearbeitung', 'angebot', 'gewonnen', 'verloren', 'erledigt'];
const STATUS_LABELS = {
  neu: 'Neu',
  in_bearbeitung: 'In Bearbeitung',
  angebot: 'Angebot raus',
  gewonnen: 'Gewonnen',
  verloren: 'Verloren',
  erledigt: 'Erledigt',
};
/* Farbton je Status — „neu" bewusst orange, damit Unbearbeitetes ins Auge springt. */
const STATUS_TONE = {
  neu:            { chip: 'bg-orange-500/20 text-orange-300',  dot: 'bg-orange-400' },
  in_bearbeitung: { chip: 'bg-sky-500/20 text-sky-300',        dot: 'bg-sky-400' },
  angebot:        { chip: 'bg-amber-500/20 text-amber-300',    dot: 'bg-amber-400' },
  gewonnen:       { chip: 'bg-green-500/20 text-green-300',    dot: 'bg-green-400' },
  verloren:       { chip: 'bg-red-500/20 text-red-300',        dot: 'bg-red-400' },
  erledigt:       { chip: 'bg-white/10 text-white/50',         dot: 'bg-white/40' },
};
const toneOf = (s) => STATUS_TONE[s] || STATUS_TONE.erledigt;

const fmtDate = (iso, withTime = true) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('de-DE', withTime
    ? { dateStyle: 'short', timeStyle: 'short' }
    : { dateStyle: 'short' });
};

function StatusChip({ status }) {
  const t = toneOf(status);
  return (
    <span className={`px-2 py-0.5 rounded text-[8px] font-mono uppercase tracking-wider shrink-0 ${t.chip}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

/* Kleine Kennzahl-Leiste über der Liste — zugleich Schnellfilter. */
function CountStrip({ counts, active, onPick }) {
  const shown = STATUS_ORDER.filter((s) => counts[s]);
  if (!shown.length) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap mb-4">
      <button
        onClick={() => onPick('all')}
        className={`px-2.5 py-1 rounded-full text-[9px] font-mono uppercase tracking-widest transition-colors ${
          active === 'all' ? 'bg-white/15 text-white' : 'bg-white/5 text-white/40 hover:text-white/70'
        }`}
      >
        Alle ({Object.values(counts).reduce((a, b) => a + b, 0)})
      </button>
      {shown.map((s) => {
        const t = toneOf(s);
        return (
          <button
            key={s}
            onClick={() => onPick(active === s ? 'all' : s)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-mono uppercase tracking-widest transition-colors ${
              active === s ? 'bg-white/15 text-white' : 'bg-white/5 text-white/40 hover:text-white/70'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
            {STATUS_LABELS[s]} ({counts[s]})
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------- Detail */

function RequestDetail({ request, onClose, onPatch, saving, lastMail }) {
  const [note, setNote] = useState('');
  const [notify, setNotify] = useState(true);
  const [copied, setCopied] = useState('');

  // Bei Wechsel auf eine andere Anfrage die Eingaben zurücksetzen.
  useEffect(() => { setNote(''); setNotify(true); }, [request?.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copy = (value, tag) => {
    try {
      navigator.clipboard.writeText(value);
      setCopied(tag);
      setTimeout(() => setCopied(''), 1500);
    } catch { /* ignore */ }
  };

  /* Notizen und Statuswechsel in EINEN Verlauf mischen, neueste zuerst. */
  const timeline = useMemo(() => {
    const items = [
      ...(request.notes || []).map((n) => ({ kind: 'note', at: n.at, by: n.by, text: n.text })),
      ...(request.history || []).map((h) => ({ kind: 'status', at: h.at, by: h.by, from: h.from, to: h.to })),
    ];
    items.push({ kind: 'created', at: request.created_at, by: request.email });
    return items.sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));
  }, [request]);

  const facts = [
    { icon: Building2,      label: 'Unternehmen', value: request.company },
    { icon: Phone,          label: 'Telefon',     value: request.phone, href: request.phone ? `tel:${request.phone}` : null },
    { icon: Euro,           label: 'Budget',      value: request.budget },
    { icon: CalendarClock,  label: 'Zeitrahmen',  value: request.timeframe },
  ].filter((f) => f.value);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center p-4 md:p-8 overflow-y-auto"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl my-auto rounded-2xl overflow-hidden"
        style={{ background: '#0e0e10', border: '1px solid rgba(255,255,255,0.1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Kopf */}
        <div className="px-6 py-5 border-b flex items-start gap-3" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="font-mono text-[9px] uppercase tracking-widest text-white/35">
                {request.reference || 'Anfrage'}
              </span>
              <StatusChip status={request.status} />
            </div>
            <h3 className="text-lg text-white truncate">{request.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <a
                href={`mailto:${request.email}`}
                className="font-mono text-[11px] text-orange-300 hover:text-orange-200 truncate"
              >
                {request.email}
              </a>
              <button
                onClick={() => copy(request.email, 'mail')}
                className="text-white/30 hover:text-white shrink-0"
                title="E-Mail kopieren"
              >
                {copied === 'mail' ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-white/40 hover:text-white shrink-0" title="Schließen">
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6 max-h-[65vh] overflow-y-auto">
          {/* Eckdaten */}
          {facts.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              {facts.map(({ icon: Icon, label, value, href }) => (
                <div key={label} className="monolith-glass p-3">
                  <div className="flex items-center gap-1.5 mb-1 text-white/35">
                    <Icon size={11} />
                    <span className="font-mono text-[8px] uppercase tracking-widest">{label}</span>
                  </div>
                  {href
                    ? <a href={href} className="text-[12px] text-white hover:text-orange-300 break-words">{value}</a>
                    : <p className="text-[12px] text-white break-words">{value}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Projekttypen + Grund */}
          {(request.project_types?.length > 0 || request.reason) && (
            <div>
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/35 mb-2">Projekt</p>
              <div className="flex flex-wrap gap-1.5">
                {request.reason && (
                  <span className="px-2.5 py-1 rounded-full text-[10px] bg-white/10 text-white/80">{request.reason}</span>
                )}
                {(request.project_types || []).map((t) => (
                  <span key={t} className="px-2.5 py-1 rounded-full text-[10px] bg-orange-500/15 text-orange-200">{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Nachricht */}
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/35 mb-2">Nachricht</p>
            <div
              className="rounded-xl p-4 text-[13px] leading-relaxed text-white/85 whitespace-pre-wrap break-words"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
            >
              {request.message || '—'}
            </div>
          </div>

          {/* Status ändern */}
          <div>
            <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/35">Status ändern</p>
              <label className="flex items-center gap-1.5 text-[10px] text-white/50 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={notify}
                  onChange={(e) => setNotify(e.target.checked)}
                  className="accent-orange-500"
                />
                {notify
                  ? <><Send size={10} /> Kunde benachrichtigen</>
                  : <><BellOff size={10} /> ohne Mail</>}
              </label>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_ORDER.map((s) => {
                const isCurrent = request.status === s;
                const t = toneOf(s);
                return (
                  <button
                    key={s}
                    disabled={isCurrent || saving}
                    onClick={() => onPatch(request.id, { status: s, notify })}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest transition-colors disabled:cursor-default ${
                      isCurrent
                        ? `${t.chip} ring-1 ring-white/20`
                        : 'bg-white/[0.04] text-white/55 hover:text-white hover:bg-white/[0.09]'
                    }`}
                  >
                    {STATUS_LABELS[s]}
                  </button>
                );
              })}
              {saving && <Loader2 size={14} className="animate-spin text-white/40 self-center ml-1" />}
            </div>
            <p className="mt-2 text-[10px] text-white/30">
              {notify
                ? 'Beim Wechsel geht automatisch eine Status-Mail an den Anfragenden.'
                : 'Es wird keine Mail verschickt.'}
            </p>
            {lastMail && (
              <p className={`mt-1.5 flex items-center gap-1.5 text-[10px] ${lastMail.ok ? 'text-green-400' : 'text-amber-400'}`}>
                {lastMail.ok ? <Check size={11} /> : <AlertCircle size={11} />}
                {lastMail.text}
              </p>
            )}
          </div>

          {/* Notiz */}
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/35 mb-2">Interne Notiz</p>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Nur intern sichtbar …"
              className="w-full rounded-xl px-3 py-2.5 text-[12px] text-white/90 placeholder:text-white/25 outline-none focus:border-white/25 resize-y"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
            />
            <button
              disabled={!note.trim() || saving}
              onClick={() => onPatch(request.id, { note, notify: false }).then(() => setNote(''))}
              className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono uppercase tracking-widest bg-white/[0.06] text-white/70 hover:text-white hover:bg-white/[0.1] disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <StickyNote size={11} />
              Notiz speichern
            </button>
          </div>

          {/* Verlauf */}
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/35 mb-2">Verlauf</p>
            <div className="space-y-2">
              {timeline.map((item, i) => (
                <div key={`${item.kind}-${item.at}-${i}`} className="flex gap-2.5 text-[11px]">
                  <span className="font-mono text-[9px] text-white/25 shrink-0 pt-0.5 w-[86px]">{fmtDate(item.at)}</span>
                  <div className="min-w-0">
                    {item.kind === 'note' && (
                      <p className="text-white/75 break-words"><StickyNote size={10} className="inline mr-1 text-white/30" />{item.text}</p>
                    )}
                    {item.kind === 'status' && (
                      <p className="text-white/60">
                        Status: {STATUS_LABELS[item.from] || item.from} → <span className="text-white">{STATUS_LABELS[item.to] || item.to}</span>
                      </p>
                    )}
                    {item.kind === 'created' && <p className="text-white/60">Anfrage eingegangen</p>}
                    {item.by && <p className="font-mono text-[9px] text-white/25">{item.by}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Fuß */}
        <div className="px-6 py-4 border-t flex items-center justify-between gap-3" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
          <a
            href={`mailto:${request.email}?subject=${encodeURIComponent(`Deine Anfrage ${request.reference || ''}`.trim())}`}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-mono uppercase tracking-widest bg-orange-500 text-black hover:bg-orange-400"
          >
            <Mail size={12} /> Antworten
            <ExternalLink size={10} className="opacity-60" />
          </a>
          <span className="font-mono text-[9px] text-white/25">
            Eingegangen {fmtDate(request.created_at)}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ View */

export default function RequestsView({ authFetch }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastMail, setLastMail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch('/api/admin/requests');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRequests(Array.isArray(data.requests) ? data.requests : []);
    } catch (e) {
      console.error('Anfragen laden fehlgeschlagen:', e);
      setError('Anfragen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const patch = useCallback(async (id, body) => {
    setSaving(true);
    setLastMail(null);
    try {
      const res = await authFetch(`/api/admin/requests/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.request) {
        setRequests((prev) => prev.map((r) => (r.id === id ? data.request : r)));
      }
      if (data.status_changed) {
        setLastMail(data.mail_sent
          ? { ok: true, text: 'Status-Mail wurde verschickt.' }
          : { ok: false, text: body.notify === false ? 'Ohne Mail gespeichert.' : 'Status gespeichert — Mail konnte nicht zugestellt werden.' });
      }
    } catch (e) {
      console.error('Anfrage speichern fehlgeschlagen:', e);
      setLastMail({ ok: false, text: 'Speichern fehlgeschlagen.' });
    } finally {
      setSaving(false);
    }
  }, [authFetch]);

  const counts = useMemo(() => {
    const c = {};
    for (const r of requests) c[r.status || 'neu'] = (c[r.status || 'neu'] || 0) + 1;
    return c;
  }, [requests]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return requests.filter((r) => {
      if (statusFilter !== 'all' && (r.status || 'neu') !== statusFilter) return false;
      if (dateFilter !== 'all' && r.created_at) {
        const d = new Date(r.created_at);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const week = new Date(today.getTime() - 7 * 864e5);
        const month = new Date(today.getTime() - 30 * 864e5);
        if (dateFilter === 'today' && d < today) return false;
        if (dateFilter === 'week' && d < week) return false;
        if (dateFilter === 'month' && d < month) return false;
      }
      if (!q) return true;
      return [r.reference, r.name, r.email, r.company, r.message, r.budget, r.reason,
        ...(r.project_types || [])]
        .some((v) => String(v ?? '').toLowerCase().includes(q));
    });
  }, [requests, search, statusFilter, dateFilter]);

  const selected = useMemo(
    () => requests.find((r) => r.id === selectedId) || null,
    [requests, selectedId],
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h2 className="text-xl text-white mb-1">Anfragen</h2>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/35">
            Projekt-Anfragen über das Kontaktformular
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg liquid-glass text-white/60 hover:text-white text-[10px] font-mono uppercase tracking-widest disabled:opacity-40"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Aktualisieren
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-xl flex items-center gap-2 text-[12px] text-red-300"
             style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <CountStrip counts={counts} active={statusFilter} onPick={setStatusFilter} />

      <AdminListView
        storageKey="requests"
        data={filtered}
        rowKey={(r) => r.id}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Anfrage suchen (Name, E-Mail, Referenz, Text…)"
        filters={[
          {
            id: 'status',
            label: 'Status',
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
              { value: 'all', label: 'Alle Status' },
              ...STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABELS[s] })),
            ],
          },
          {
            id: 'date',
            label: 'Zeitraum',
            value: dateFilter,
            onChange: setDateFilter,
            options: [
              { value: 'all',   label: 'Alle Zeit' },
              { value: 'today', label: 'Heute' },
              { value: 'week',  label: 'Diese Woche' },
              { value: 'month', label: 'Dieser Monat' },
            ],
          },
        ]}
        groupByStatus
        groupKey={(r) => r.status || 'neu'}
        groupLabels={STATUS_LABELS}
        emptyText={loading ? 'Lade Anfragen…' : 'Keine Anfragen'}
        emptyIcon={Inbox}
        onRowClick={(r) => { setLastMail(null); setSelectedId(r.id); }}
        columns={[
          {
            id: 'name',
            label: 'Anfrage',
            locked: true,
            render: (r) => (
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-white truncate">{r.name}</span>
                <StatusChip status={r.status || 'neu'} />
              </div>
            ),
          },
          { id: 'reference', label: 'Referenz', mono: true, muted: true, render: (r) => r.reference || '—' },
          { id: 'email',     label: 'E-Mail',   mono: true, render: (r) => r.email || '—' },
          { id: 'company',   label: 'Firma',    defaultVisible: false, render: (r) => r.company || '—' },
          {
            id: 'types',
            label: 'Projekt',
            render: (r) => (r.project_types?.length
              ? <span className="text-white/70 truncate">{r.project_types.join(', ')}</span>
              : <span className="text-white/25">—</span>),
          },
          { id: 'budget', label: 'Budget', mono: true, muted: true, defaultVisible: false, render: (r) => r.budget || '—' },
          {
            id: 'message',
            label: 'Nachricht',
            muted: true,
            defaultVisible: false,
            render: (r) => (
              <span className="block max-w-[280px] truncate" title={r.message}>{r.message || '—'}</span>
            ),
          },
          { id: 'date', label: 'Datum', mono: true, muted: true, render: (r) => fmtDate(r.created_at) },
        ]}
        rowActions={(r) => (
          <a
            href={`mailto:${r.email}`}
            onClick={(e) => e.stopPropagation()}
            className="p-1.5 text-white/40 hover:text-white transition-colors inline-flex"
            title="Antworten"
          >
            <Mail size={13} />
          </a>
        )}
      />

      {selected && (
        <RequestDetail
          request={selected}
          onClose={() => setSelectedId(null)}
          onPatch={patch}
          saving={saving}
          lastMail={lastMail}
        />
      )}
    </div>
  );
}
